import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { apiurl } from "../config/config";
import { FaFileExcel } from "react-icons/fa";
import { motion } from "framer-motion";
import { IoMdClose } from "react-icons/io";
import Pagination from "../components/pagination";
import { SuwasthiLoader } from "../components/loader";
import Layout, { Container } from "../components/layout";

const OnboardingList = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalItems, setTotalItems] = useState(0);
  const [responseList, setResponseList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal visibility state
  const [search, setSearch] = useState("");

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${apiurl}/api/application/filter?applicationStatus=onboarding&page=${currentPage}&limit=${itemsPerPage}`
      );
      setApplications(data.applications);
    } catch (err) {
      console.error("Error fetching applications:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEmployee = async () => {
    if (selectedApplications.length === 0) {
      toast.error(
        "Please select at least one application to create an employee."
      );
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${apiurl}/api/employee/createBulkEmployeeFromApplication`,
        { applicationObjIds: selectedApplications }
      );

      if (response.status === 201) {
        toast.success("Employee(s) created successfully ✅");
        setResponseList(response.data.results); // Set the response data for the modal
        setIsModalOpen(true); // Open the modal
        fetchApplications(); // Refresh data
      } else {
        toast.error("Some employees could not be created ❌");
      }
    } catch (err) {
      console.error("Error creating employees:", err);
      toast.error("Something went wrong while creating the employees.");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (appId) => {
    setSelectedApplications((prevSelected) =>
      prevSelected.includes(appId)
        ? prevSelected.filter((id) => id !== appId)
        : [...prevSelected, appId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedApplications([]);
    } else {
      setSelectedApplications(applications.map((app) => app._id));
    }
    setSelectAll(!selectAll);
  };

  const filteredApps = applications.filter((app) => {
    return (
      app?.candidateSnapshot?.firstName
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      app?.candidateSnapshot?.lastName
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      app?.candidateSnapshot?.email.toLowerCase().includes(search.toLowerCase())
    );
  });

  useEffect(() => {
    fetchApplications();
  }, [currentPage, itemsPerPage]);

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="p-2 bg-gray-100">
          <ToastContainer />
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
            <input
              type="text"
              placeholder="Search by name or email"
              className="p-2 border rounded w-full md:w-1/3"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="p-2 border rounded"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              {/* <option value="50">50</option> */}
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="500">500</option>
              <option value="1000">1000</option>
            </select>
            {/* {selectedApplications.length > 0 && (
              <button
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2"
                onClick={handleCreateEmployee}
              >
                Create Employee
              </button>
            )} */}
          </div>

          {loading ? (
            <SuwasthiLoader />
          ) : (
            <div className="bg-white shadow-md rounded-lg p-4 overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="p-3">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                      {selectedApplications.length}
                    </th>
                    <th className="p-3 text-left">Application ID</th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Employee ID</th>
                    <th className="p-3 text-left">Manager</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map((app, index) => (
                    <motion.tr
                      key={app._id}
                      className="border-b hover:bg-gray-50"
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        visible: (index) => ({
                          opacity: 1,
                          y: 0,
                          transition: { delay: index * 0.1, duration: 0.4 },
                        }),
                      }}
                      initial="hidden"
                      animate="visible"
                      custom={index}
                    >
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedApplications.includes(app._id)}
                          onChange={() => handleCheckboxChange(app._id)}
                        />
                      </td>
                      <td className="p-3">{app?.id}</td>
                      <td className="p-3">
                        {app?.candidateSnapshot?.firstName}{" "}
                        {app?.candidateSnapshot?.lastName}
                      </td>
                      <td className="p-3">{app?.candidateSnapshot?.email}</td>
                      <td className="p-3">{app?.applicationStatus}</td>
                      <td className="p-3">{app?.employeeId}</td>
                      <td className="p-3">{app?.reportingManagerName}</td>
                      <td className="p-3 flex gap-2">
                        <Link
                          className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                          to={`/applications/${app._id}/manage`}
                        >
                          Manage
                        </Link>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
          />

          {/* Modal for displaying the response list */}
          {isModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
              <div className="bg-white p-6 rounded-lg w-3/4 max-w-2xl">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">
                    Employee Creation Results
                  </h2>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <IoMdClose size={24} />
                  </button>
                </div>

                <div className="space-y-4">
                  {responseList.map((result, index) => (
                    <div
                      key={index}
                      className="border p-4 rounded-lg shadow-md"
                    >
                      <p>
                        <strong>Application ID:</strong>{" "}
                        {result.applicationObjId}
                      </p>
                      <p>
                        <strong>Employee ID:</strong> {result.employeeId}
                      </p>
                      <p>
                        <strong>Employee Code:</strong> {result.employeeCode}
                      </p>
                      <p>
                        <strong>Email:</strong> {result.email}
                      </p>
                      <p>
                        <strong>Joining Link:</strong>{" "}
                        <a
                          href={result.joiningLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500"
                        >
                          {result.joiningLink}
                        </a>
                      </p>
                      <p>
                        <strong>Message:</strong> {result.message}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </Container>
    </Layout>
  );
};

export default OnboardingList;